import { useEffect, useMemo, useRef, useState } from "react";
import {
  activateConsultantAccess,
  createConsultantRecoveryLink,
  listConsultantAccess,
  setConsultantAdminRole,
} from "../api";
import { filterConsultantAccess, type ConsultantAccessFilter } from "../filter";
import type { ConsultantAccessLinkResult, ConsultantAccessRow } from "../types";

const STATE_LABEL = {
  directory_only: "Assignment only",
  active: "Platform access",
  inactive: "Inactive",
} as const;

const ROLE_LABEL = {
  user: "Consultant",
  admin: "Admin",
  super_admin: "Super Admin",
} as const;

function dateTime(value: string | null): string {
  if (!value) return "Never";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value)
  );
}

export function ConsultantAccessAdmin({
  canManageRoles,
}: {
  canManageRoles: boolean;
}): JSX.Element {
  const [rows, setRows] = useState<ConsultantAccessRow[]>([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ConsultantAccessFilter>("directory_only");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<ConsultantAccessLinkResult | null>(null);
  const linkRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<{
    row: ConsultantAccessRow;
    role: "user" | "admin";
  } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      setRows(await listConsultantAccess());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Consultant access could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!link) return;
    const frame = window.requestAnimationFrame(() => {
      linkRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [link]);

  const visible = useMemo(() => filterConsultantAccess(rows, query, filter), [rows, query, filter]);
  const directoryCount = rows.filter((row) => row.access_state === "directory_only").length;
  const activeCount = rows.filter((row) => row.access_state === "active").length;
  const adminCount = rows.filter((row) => row.role === "admin").length;

  const act = async (row: ConsultantAccessRow) => {
    setBusyId(row.user_id);
    setError(null);
    setLink(null);
    setCopied(false);
    try {
      const result =
        row.access_state === "active"
          ? await createConsultantRecoveryLink(row.user_id)
          : await activateConsultantAccess(row.user_id);
      if (row.access_state !== "active" && filter === "directory_only") {
        setFilter("all");
      }
      setLink(result);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Consultant access request failed.");
    } finally {
      setBusyId(null);
    }
  };

  const changeRole = async () => {
    if (!pendingRole) return;
    const { row, role } = pendingRole;
    setBusyId(row.user_id);
    setError(null);
    setNotice(null);
    try {
      const result = await setConsultantAdminRole(row.user_id, role);
      setPendingRole(null);
      await load();
      setNotice(
        role === "admin"
          ? `${result.consultant.display_name} is now an Admin.`
          : `${result.consultant.display_name} is now a consultant and no longer has Admin permissions.`
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Consultant role could not be changed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <details className="settings-disclosure consultant-access" aria-label="Consultant user access">
      <summary className="settings-disclosure__summary">
        <span>
          <strong>Consultant user access</strong>
          <small>
            Activate sign-in and create one-time password links without sharing passwords.
          </small>
        </span>
        <span className="settings-disclosure__summary-meta">
          {loading
            ? "Loading"
            : `${directoryCount} awaiting access · ${activeCount} active · ${adminCount} admins`}
          <span className="settings-disclosure__chevron" aria-hidden="true">
            ⌄
          </span>
        </span>
      </summary>

      <div className="settings-disclosure__body consultant-access__body">
        <p className="consultant-roster__intro">
          Assignment-only consultants can receive signals but cannot sign in. Admins can activate
          access and create one-time password reset links. Admin role changes are available only to
          the Super Admin and do not change a consultant&apos;s contacts or assignments. BDSE never
          displays or stores passwords.
        </p>

        <div className="consultant-access__toolbar">
          <input
            type="search"
            className="sx-field"
            placeholder="Search name, email, title, practice, or role"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <select
            className="sx-field"
            aria-label="Access status"
            value={filter}
            onChange={(event) => setFilter(event.target.value as ConsultantAccessFilter)}
          >
            <option value="directory_only">Awaiting platform access</option>
            <option value="active">Active users</option>
            <option value="inactive">Inactive users</option>
            <option value="all">All consultants</option>
          </select>
          <button
            type="button"
            className="sx-btn sx-btn-p3"
            disabled={loading}
            onClick={() => void load()}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {error ? (
          <p className="consultant-access__message consultant-access__message--error" role="alert">
            {error}
          </p>
        ) : null}

        {notice ? (
          <p
            className="consultant-access__message consultant-access__message--success"
            role="status"
          >
            {notice}
          </p>
        ) : null}

        {pendingRole ? (
          <div className="consultant-access__confirm" role="alertdialog" aria-modal="false">
            <div>
              <strong>{pendingRole.role === "admin" ? "Make Admin?" : "Remove Admin role?"}</strong>
              <p>
                {pendingRole.role === "admin"
                  ? `${pendingRole.row.display_name} will be able to use BDSE administrator features.`
                  : `${pendingRole.row.display_name} will keep platform access, contacts, and assignments, but will lose administrator features.`}
              </p>
            </div>
            <div className="consultant-access__confirm-actions">
              <button
                type="button"
                className="sx-btn sx-btn-p3"
                disabled={busyId !== null}
                onClick={() => setPendingRole(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sx-btn sx-btn-p2"
                disabled={busyId !== null}
                onClick={() => void changeRole()}
              >
                {busyId ? "Saving…" : pendingRole.role === "admin" ? "Make Admin" : "Remove Admin"}
              </button>
            </div>
          </div>
        ) : null}

        {!loading && visible.length === 0 ? (
          <p className="consultant-access__message">No consultants match this view.</p>
        ) : (
          <ul className="consultant-access__list">
            {visible.map((row) => (
              <li key={row.user_id} className="consultant-access__row">
                <div>
                  <strong>{row.display_name}</strong>
                  <span>{row.email}</span>
                  <small>
                    {[row.title, row.primary_practice].filter(Boolean).join(" · ") || "Consultant"}
                  </small>
                </div>
                <dl>
                  <div>
                    <dt>Status</dt>
                    <dd>{STATE_LABEL[row.access_state]}</dd>
                  </div>
                  <div>
                    <dt>Last sign-in</dt>
                    <dd>{dateTime(row.last_sign_in_at)}</dd>
                  </div>
                  <div>
                    <dt>Role</dt>
                    <dd>{ROLE_LABEL[row.role]}</dd>
                  </div>
                </dl>
                <div className="consultant-access__actions">
                  {row.role === "user" && row.access_state !== "inactive" ? (
                    <button
                      type="button"
                      className="sx-btn sx-btn-p3"
                      disabled={busyId !== null}
                      onClick={() => void act(row)}
                    >
                      {busyId === row.user_id
                        ? "Creating…"
                        : row.access_state === "active"
                          ? "Create reset link"
                          : "Activate & create link"}
                    </button>
                  ) : null}
                  {canManageRoles && row.role === "user" ? (
                    <button
                      type="button"
                      className="sx-btn sx-btn-p3"
                      disabled={busyId !== null || row.access_state !== "active"}
                      title={
                        row.access_state === "active"
                          ? "Grant administrator permissions"
                          : "Activate platform access before making this consultant an Admin"
                      }
                      onClick={() => setPendingRole({ row, role: "admin" })}
                    >
                      Make Admin
                    </button>
                  ) : canManageRoles && row.role === "admin" ? (
                    <button
                      type="button"
                      className="sx-btn sx-btn-p3"
                      disabled={busyId !== null}
                      onClick={() => setPendingRole({ row, role: "user" })}
                    >
                      Remove Admin
                    </button>
                  ) : canManageRoles && row.role === "super_admin" ? (
                    <span className="consultant-access__protected">Protected role</span>
                  ) : null}
                </div>
                {link?.consultant.user_id === row.user_id ? (
                  <div
                    ref={linkRef}
                    className="consultant-access__link consultant-access__link--inline"
                    role="status"
                  >
                    <strong>One-time link for {link.consultant.display_name}</strong>
                    <p>Copy and send this link privately. It is shown only for this request.</p>
                    <div>
                      <input
                        readOnly
                        value={link.action_link}
                        aria-label={`One-time activation or reset link for ${link.consultant.display_name}`}
                      />
                      <button
                        type="button"
                        className="sx-btn sx-btn-p2"
                        onClick={() => {
                          void navigator.clipboard?.writeText(link.action_link);
                          setCopied(true);
                        }}
                      >
                        {copied ? "Copied" : "Copy link"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}
